import { useState } from "preact/compat"
import { LedgerAccount } from "@/api"
import { api_fetch } from "@/api/client"
import { IntegrationWithJobInfo, updateIntegration, WalmartConfig } from "@/api/integration"
import { LedgerAccountSelect } from "@/components/ledger_account_select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"

interface WalmartAllowanceSettingsProps {
  integration: IntegrationWithJobInfo
}

/** The rate as the server should store it: a positive percentage, or null when blank or invalid. */
export function parse_allowance_percentage(raw: string): number | null {
  const value = parseFloat(raw)
  return Number.isFinite(value) && value > 0 ? value : null
}

/**
 * Walmart's off-invoice allowance write-off. Walmart books each invoice net of the allowances in
 * the supplier agreement and pays that figure, so its remittance lists no discount and every
 * invoice comes up short by the same rate. With an account and rate set, Apply Cash writes each
 * invoice's allowance-sized short off to the account on the credit memo (the same mechanism as
 * KeHE's BI allowance).
 */
export function WalmartAllowanceSettings({ integration }: WalmartAllowanceSettingsProps) {
  const config = integration.data as unknown as WalmartConfig
  const [accounts, setAccounts] = useState<LedgerAccount[]>([])
  const [account, setAccount] = useState<string | null>(config.allowance_account ?? null)
  const [percentage, setPercentage] = useState<string>(config.allowance_percentage?.toString() ?? "")
  const [saving, setSaving] = useState(false)

  useAsyncEffect(async () => {
    const res = await api_fetch<LedgerAccount[]>("/reason_codes")
    if (res.ok) {
      setAccounts(res.value.data)
    }
  }, [integration.id])

  const parsed_percentage = parse_allowance_percentage(percentage)
  // An account without a rate would silently book nothing, so it cannot be saved that way.
  const incomplete = account !== null && parsed_percentage === null

  async function handle_save() {
    setSaving(true)
    const data: WalmartConfig = { allowance_account: account, allowance_percentage: account ? parsed_percentage : null }
    const res = await updateIntegration(integration.id, { data })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: "Could not save the Walmart allowance.", variant: "destructive" })
      return
    }
    toast({ title: "Settings saved", description: "Walmart allowance updated." })
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">
        Walmart pays each invoice net of the allowances in your supplier agreement and does not list them on the
        remittance, so without this every invoice is left open by that amount. With an account and rate set, Apply Cash
        writes each invoice's allowance-sized short off to the account on the credit memo. A short wider than the
        allowance books the allowance and flags the rest.
      </p>
      <div class="space-y-1 max-w-md">
        <Label htmlFor="walmart_allowance_account">Allowance account</Label>
        <LedgerAccountSelect
          id="walmart_allowance_account"
          value={account}
          onValueChange={setAccount}
          accounts={accounts}
          none_label="Not set — leave the short open"
        />
      </div>
      {account ? (
        <div class="space-y-1 max-w-md">
          <Label htmlFor="walmart_allowance_percentage">Allowance (% of invoice)</Label>
          <Input
            id="walmart_allowance_percentage"
            type="number"
            step="0.01"
            placeholder="2.50"
            value={percentage}
            onInput={(e: Event) => setPercentage((e.target as HTMLInputElement).value)}
          />
        </div>
      ) : null}
      <Button variant="outline" disabled={saving || incomplete} onClick={handle_save}>
        {saving ? "Saving…" : "Save allowance"}
      </Button>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("parse_allowance_percentage", () => {
    it("keeps a positive rate and drops anything else", () => {
      expect(parse_allowance_percentage("2.5")).toBe(2.5)
      expect(parse_allowance_percentage("")).toBeNull()
      expect(parse_allowance_percentage("0")).toBeNull()
      expect(parse_allowance_percentage("-1")).toBeNull()
      expect(parse_allowance_percentage("abc")).toBeNull()
    })
  })
}
